import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Configuration
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of inactivity before warning
const WARNING_DURATION_MS = 60 * 1000; // 60 seconds warning before logout
const CHECK_INTERVAL_MS = 1000; // Check every second

// localStorage keys for cross-tab synchronization
const STORAGE_KEYS = {
  LAST_ACTIVITY: 'session_last_activity',
  EXPIRES_AT: 'session_expires_at',
};

const API = process.env.REACT_APP_BACKEND_URL;

// Public-facing route patterns. On these routes:
// - Inactivity timer still runs if user is logged in
// - Warning banner shows when session is about to expire
// - When session expires, user is logged out but STAYS on the page (no redirect)
const PUBLIC_ROUTE_MATCHERS = [
  /^\/$/,
  /^\/pricing\/?$/,
  /^\/legal(\/|$)/,
  /^\/p\//,
  /^\/legacy-home\/?$/,
  /^\/legacy-pricing\/?$/,
  /^\/help(\/|$)/,
  /^\/job-board(\/|$)/,
  /^\/t\//,                          // company-scoped public careers/jobs/apply
  /^\/careers(\/|$)/,
  /^\/quotes\/verify/,
  /^\/service-agreements\/verify/,
  /^\/sign\//,
  /^\/interview\//,
  /^\/referee\//,
  /^\/verify\//,
  /^\/login\/?$/,
  /^\/signup\/?$/,
  /^\/set-password\/?$/,
  /^\/reset-password\/?$/,
  /^\/account-unavailable\/?$/,
  /^\/applicant-portal\/?$/,         // login screen only; deeper applicant routes stay protected
  /^\/applicant\/?$/,
  /^\/book-demo(\/|$)/,              // Demo booking pages
  /^\/demo\/tour\//,                 // Demo tour player
  /^\/r\/goal\//,                    // Public goal report shares (snapshot-frozen)
];

function isPublicRoute(pathname) {
  return PUBLIC_ROUTE_MATCHERS.some((re) => re.test(pathname || '/'));
}

const InactivityLogout = ({ children }) => {
  const location = useLocation();
  const onPublicRoute = isPublicRoute(location.pathname);
  const onPublicRouteRef = useRef(onPublicRoute);
  useEffect(() => { onPublicRouteRef.current = onPublicRoute; }, [onPublicRoute]);

  // Track login state as a reactive value so effects re-run on login/logout
  const [loggedIn, setLoggedIn] = useState(
    () => !!localStorage.getItem('access_token') || !!localStorage.getItem('applicant_token')
  );
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_DURATION_MS / 1000);
  // Track if session just expired on public page (show info banner instead of warning)
  const [sessionExpiredOnPublic, setSessionExpiredOnPublic] = useState(false);
  const checkIntervalRef = useRef(null);
  const isLoggingOutRef = useRef(false);
  const showWarningRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // CRITICAL: Clear stale session data on mount when not logged in
  // This prevents the timer from running for anonymous users with leftover localStorage data
  useEffect(() => {
    const hasToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('applicant_token');
    if (!hasToken) {
      // User is not logged in - clear any stale session tracking data
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    }
  }, []); // Run once on mount

  // Sync login state from localStorage (covers same-tab and cross-tab changes)
  useEffect(() => {
    const syncLoginState = () => {
      const hasToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('applicant_token');
      setLoggedIn(prev => (prev !== hasToken ? hasToken : prev));
    };
    // Cross-tab: storage event fires when another tab modifies localStorage
    window.addEventListener('storage', syncLoginState);
    // Same-tab: poll since storage event doesn't fire for same-tab changes
    const poll = setInterval(syncLoginState, 2000);
    return () => {
      window.removeEventListener('storage', syncLoginState);
      clearInterval(poll);
    };
  }, []);

  const isApplicantSession = useCallback(() => {
    return !!localStorage.getItem('applicant_token') && !localStorage.getItem('access_token');
  }, []);

  const getLastActivity = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    return stored ? parseInt(stored, 10) : Date.now();
  }, []);

  const getExpiresAt = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
    return stored ? parseInt(stored, 10) : null;
  }, []);

  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());
    if (!showWarningRef.current) {
      localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    }
  }, []);

  const setExpirationTime = useCallback(() => {
    const expiresAt = Date.now() + WARNING_DURATION_MS;
    localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
    return expiresAt;
  }, []);

  const handleLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    const isApplicant = isApplicantSession();
    const redirectPath = isApplicant ? '/applicant-portal' : '/login';
    const token = localStorage.getItem('access_token') || localStorage.getItem('applicant_token');

    if (token && API) {
      try {
        fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {});
      } catch {}
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('applicant_token');
    localStorage.removeItem('user');
    localStorage.removeItem('applicant_selected_company');
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    // Clear impersonation session on logout
    localStorage.removeItem('original_superadmin_token');

    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    // On public routes: show "session expired" banner but DON'T redirect
    // User stays on the page and can continue browsing anonymously
    if (onPublicRouteRef.current) {
      setShowWarning(false);
      setSessionExpiredOnPublic(true);
      setLoggedIn(false);
      setTimeout(() => { isLoggingOutRef.current = false; }, 0);
      return;
    }

    // On protected routes: redirect to login
    window.location.href = `${redirectPath}?session_expired=true`;
  }, [isApplicantSession]);

  const checkSessionStatus = useCallback(() => {
    if (!loggedIn) {
      setShowWarning(false);
      return;
    }

    const now = Date.now();
    const expiresAt = getExpiresAt();
    const lastActivity = getLastActivity();

    // Check if warning countdown has expired - trigger logout
    if (expiresAt) {
      if (now >= expiresAt) {
        handleLogout();
        return;
      }
      const remaining = Math.max(0, Math.ceil((expiresAt - now) / 1000));
      setRemainingSeconds(remaining);
      setShowWarning(true);
      return;
    }

    // Check if inactivity threshold reached - start warning countdown
    const inactiveTime = now - lastActivity;
    if (inactiveTime >= INACTIVITY_TIMEOUT_MS) {
      const newExpiresAt = setExpirationTime();
      const remaining = Math.ceil((newExpiresAt - now) / 1000);
      setRemainingSeconds(remaining);
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [loggedIn, getExpiresAt, getLastActivity, setExpirationTime, handleLogout]);

  const extendSession = useCallback(() => {
    updateLastActivity();
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    setShowWarning(false);
    setRemainingSeconds(WARNING_DURATION_MS / 1000);
  }, [updateLastActivity]);

  // Route-change reaction: clear session expired banner when navigating away from public page
  useEffect(() => {
    // If user navigates away from public page, clear the "session expired" info banner
    if (!onPublicRoute && sessionExpiredOnPublic) {
      setSessionExpiredOnPublic(false);
    }
  }, [onPublicRoute, sessionExpiredOnPublic]);

  // Re-check on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkSessionStatus]);

  // Activity listeners — re-runs when loggedIn changes (login/logout transition)
  useEffect(() => {
    if (!loggedIn) return;

    // ALWAYS reset activity timestamp on setup.
    // This prevents stale values from a previous session from triggering
    // an immediate warning (the old "60-second trigger" bug).
    updateLastActivity();
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    let throttleTimeout = null;
    const handleActivity = () => {
      if (throttleTimeout) return;
      if (showWarningRef.current) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        updateLastActivity();
      }, 1000);
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [loggedIn, updateLastActivity]);

  // Check interval — re-runs when loggedIn changes
  useEffect(() => {
    if (!loggedIn) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    checkSessionStatus();
    checkIntervalRef.current = setInterval(checkSessionStatus, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [loggedIn, checkSessionStatus]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const dismissExpiredBanner = useCallback(() => {
    setSessionExpiredOnPublic(false);
  }, []);

  return (
    <>
      {/* Warning banner - shows on ALL pages when session is about to expire */}
      {/* Rendered via portal to ensure it's always above modal overlays */}
      {showWarning && ReactDOM.createPortal(
        <div data-session-banner="true" className="fixed top-0 left-0 right-0 z-[999999] animate-in slide-in-from-top duration-300 pointer-events-auto" style={{ isolation: 'isolate' }}>
          <div className="bg-slate-900/95 backdrop-blur-sm text-white shadow-xl border-b border-amber-500/50">
            <div className="container mx-auto px-4 py-2">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <p className="text-sm">
                    Session expires in{' '}
                    <span className="font-bold text-amber-400">{formatTime(remainingSeconds)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={extendSession}
                    size="sm"
                    className="h-7 px-3 text-xs bg-amber-500 hover:bg-amber-600 text-white font-medium pointer-events-auto"
                    data-testid="stay-logged-in-btn"
                  >
                    Stay Logged In
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-3 text-xs text-slate-400 hover:text-white hover:bg-slate-800 pointer-events-auto"
                    data-testid="logout-now-btn"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            </div>
            <div className="h-0.5 bg-slate-700">
              <div
                className="h-full bg-amber-500 transition-all duration-1000 ease-linear"
                style={{ width: `${(remainingSeconds / (WARNING_DURATION_MS / 1000)) * 100}%` }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Session expired info banner - shows on PUBLIC pages after auto-logout */}
      {/* Also rendered via portal to ensure it's always above modal overlays */}
      {sessionExpiredOnPublic && !showWarning && ReactDOM.createPortal(
        <div data-session-banner="true" className="fixed top-0 left-0 right-0 z-[999999] animate-in slide-in-from-top duration-300 pointer-events-auto" style={{ isolation: 'isolate' }}>
          <div className="bg-slate-800/95 backdrop-blur-sm text-white shadow-xl border-b border-slate-600/50">
            <div className="container mx-auto px-4 py-2">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-slate-400" />
                  <p className="text-sm text-slate-300">
                    Your session has expired. You can continue browsing or{' '}
                    <a href="/login" className="text-blue-400 hover:text-blue-300 underline pointer-events-auto">
                      log in again
                    </a>.
                  </p>
                </div>
                <Button
                  onClick={dismissExpiredBanner}
                  variant="ghost"
                  size="sm"
                  className="h-7 px-3 text-xs text-slate-400 hover:text-white hover:bg-slate-700 pointer-events-auto"
                  data-testid="dismiss-expired-btn"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className={(showWarning || sessionExpiredOnPublic) ? 'pt-12' : ''}>
        {children}
      </div>
    </>
  );
};

export default InactivityLogout;
